import React, { useState, useEffect } from 'react';

const HostChecker = () => {
  const [domains, setDomains] = useState([]);
  const [newDomain, setNewDomain] = useState('');
  const [newExpirationDate, setNewExpirationDate] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  // Use sessionStorage to store a simple user identifier
  useEffect(() => {
    if (!sessionStorage.getItem('user-id')) {
      const userId = `guest-${Date.now()}`;
      sessionStorage.setItem('user-id', userId);
    }
  }, []);

  const fetchDomains = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/hostchecker/domains`);
      if (!response.ok) {
        throw new Error('Network response was not ok');
      }
      const data = await response.json();
      setDomains(data);
      setErrorMessage('');
    } catch (e) {
      console.error("Error fetching domains:", e);
      setErrorMessage("Failed to load domains. Please ensure the backend server is running.");
    }
  };

  useEffect(() => {
    fetchDomains();
  }, []);

  const handleAddDomain = async () => {
    if (!newDomain || !newExpirationDate) {
      setErrorMessage("Please fill in both the domain and expiration date.");
      return;
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/hostchecker/domains`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ domain: newDomain, expiration_date: newExpirationDate })
      });

      if (!response.ok) {
        throw new Error('Failed to add domain');
      }

      setNewDomain('');
      setNewExpirationDate('');
      setErrorMessage('');
      fetchDomains(); // Refresh the list
    } catch (e) {
      console.error("Error adding domain:", e);
      setErrorMessage("Failed to add domain. Please try again.");
    }
  };

  const handleDeleteDomain = async (id) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/hostchecker/domains/${id}`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        throw new Error('Failed to delete domain');
      }

      setErrorMessage('');
      fetchDomains(); // Refresh the list
    } catch (e) {
      console.error("Error deleting domain:", e);
      setErrorMessage("Failed to delete domain. Please try again.");
    }
  };

  const getDaysRemaining = (dateString) => {
    const now = new Date();
    const expirationDate = new Date(dateString);
    const diffTime = expirationDate.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  return (
    <div className="min-h-screen bg-gray-900 text-gray-200 p-8 font-sans flex flex-col items-center">
      <div className="w-full max-w-4xl">
        <header className="text-center mb-8">
          <h1 className="text-5xl font-bold mb-2 text-white">Hosting Expiration Monitor</h1>
          <p className="text-gray-400">Manage and track your domains to avoid expiration.</p>
        </header>

        <section className="bg-gray-800 p-6 rounded-2xl shadow-lg mb-8 border border-gray-700">
          <h2 className="text-2xl font-semibold mb-4 text-white">Add a New Domain</h2>
          <div className="flex flex-col sm:flex-row gap-4">
            <input
              type="text"
              className="flex-1 p-3 rounded-xl bg-gray-700 text-white placeholder-gray-500 border border-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Domain Name (e.g., onehub.com)"
              value={newDomain}
              onChange={(e) => setNewDomain(e.target.value)}
            />
            <input
              type="date"
              className="flex-1 p-3 rounded-xl bg-gray-700 text-white border border-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={newExpirationDate}
              onChange={(e) => setNewExpirationDate(e.target.value)}
            />
            <button
              onClick={handleAddDomain}
              className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 px-6 rounded-xl transition-all duration-300 transform hover:scale-105 shadow-md"
            >
              Add Domain
            </button>
          </div>
          {errorMessage && (
            <p className="text-red-500 text-sm mt-4 text-center">{errorMessage}</p>
          )}
        </section>

        <section className="bg-gray-800 p-6 rounded-2xl shadow-lg border border-gray-700">
          <h2 className="text-2xl font-semibold mb-4 text-white">Domain List</h2>
          {domains.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="min-w-full table-auto">
                <thead>
                  <tr className="bg-gray-700 text-gray-400 uppercase text-sm leading-normal">
                    <th className="py-3 px-6 text-left rounded-tl-xl">Domain</th>
                    <th className="py-3 px-6 text-left">Expiration Date</th>
                    <th className="py-3 px-6 text-left">Days Remaining</th>
                    <th className="py-3 px-6 text-center rounded-tr-xl">Actions</th>
                  </tr>
                </thead>
                <tbody className="text-gray-300 text-sm font-light">
                  {domains.map((item) => {
                    const daysRemaining = getDaysRemaining(item.expiration_date);
                    const isExpiringSoon = daysRemaining <= 30;
                    return (
                      <tr key={item._id} className={`border-b border-gray-700 hover:bg-gray-700 transition-colors duration-200 ${isExpiringSoon ? 'bg-red-900/40 text-red-300' : ''}`}>
                        <td className="py-4 px-6 whitespace-nowrap">{item.domain}</td>
                        <td className="py-4 px-6 whitespace-nowrap">{new Date(item.expiration_date).toLocaleDateString()}</td>
                        <td className="py-4 px-6 whitespace-nowrap font-bold">
                          {isExpiringSoon ? (
                            <span className="text-red-400">⚠️ {daysRemaining} days left!</span>
                          ) : (
                            `${daysRemaining} days`
                          )}
                        </td>
                        <td className="py-4 px-6 text-center">
                          <button
                            onClick={() => handleDeleteDomain(item._id)}
                            className="bg-red-600 hover:bg-red-700 text-white font-bold py-1 px-4 rounded-full transition-colors duration-200 text-xs"
                          >
                            Delete
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-center text-gray-500 mt-4">No domains added yet.</p>
          )}
        </section>
      </div>
    </div>
  );
};

export default HostChecker;
